import { DataProvider, HttpError } from 'react-admin';

import { requirePartner } from '@src/providers/dataProvider/shared';

import { fetchFacets } from './fetchFacets';
import type { FacetGroup } from './types';

interface FacetsDataProvider {
  getList: () => Promise<{ data: FacetGroup[]; total: number }>;
  getOne: (resource: string, params: { id: string }) => Promise<{ data: FacetGroup }>;
  getMany: () => Promise<never>;
  getManyReference: () => Promise<never>;
  create: () => Promise<never>;
  update: () => Promise<never>;
  updateMany: () => Promise<never>;
  delete: () => Promise<never>;
  deleteMany: () => Promise<never>;
}

/**
 * Data provider for facets resource.
 *
 * Facets are data-only (no UI) and provide valid filter values for pricing rules.
 * getList returns all facet groups, getOne returns a single facet group by tag.
 */
const facetsDataProviderImpl: FacetsDataProvider = {
  getList: async () => {
    try {
      const partner = requirePartner();
      const data = await fetchFacets({ partnerId: partner.id });

      return {
        data,
        total: data.length,
      };
    } catch (error) {
      console.error('facetsDataProvider.getList failed:', {
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load facets.', status);
    }
  },

  getOne: async (resource, params) => {
    try {
      const partner = requirePartner();
      const facets = await fetchFacets({ partnerId: partner.id });
      const tag = params.id;
      const facetGroup = facets.find((fg) => fg.tag === tag);

      if (!facetGroup) {
        return {
          data: { id: tag, tag, values: [] },
        };
      }

      return { data: facetGroup };
    } catch (error) {
      console.error('facetsDataProvider.getOne failed:', {
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load facet.', status);
    }
  },

  getMany: async () => {
    throw new Error('getMany not implemented for facets resource');
  },

  getManyReference: async () => {
    throw new Error('getManyReference not implemented for facets resource');
  },

  create: async () => {
    throw new Error('create not implemented for facets resource');
  },

  update: async () => {
    throw new Error('update not implemented for facets resource');
  },

  updateMany: async () => {
    throw new Error('updateMany not implemented for facets resource');
  },

  delete: async () => {
    throw new Error('delete not implemented for facets resource');
  },

  deleteMany: async () => {
    throw new Error('deleteMany not implemented for facets resource');
  },
};

// Cast to DataProvider for react-admin compatibility.
// React-admin's DataProvider methods are generic, which prevents type-safe
// single-resource providers. We define proper types above and cast here.
export const facetsDataProvider = facetsDataProviderImpl as unknown as DataProvider;

export default facetsDataProvider;
